import { a4Stylesheet } from './a4Stylesheet';
import { esc, renderA4Body, type BusinessIdentity } from './renderA4';
import { A4_HEIGHT_MM } from './geometry';
import { A4_WIDTH_PX } from './preview';
import { modeClass } from './renderMode';
import type { NormalizedDocument } from './types';

import { buildQuotationPrintPackage } from '@domain/quotationPackage';

function letterhead(biz: BusinessIdentity) {
  const logo = biz.logoDataUrl ? `<img class="doc-logo" src="${esc(biz.logoDataUrl)}" alt="">` : '';
  return `<header class="q-letterhead">${logo}<strong>${esc(biz.legalName)}</strong>${biz.addressLines.map((line) => `<div>${esc(line)}</div>`).join('')}${biz.gstin ? `<div>GSTIN: ${esc(biz.gstin)}</div>` : ''}</header>`;
}

function coverPage(m: NormalizedDocument, biz: BusinessIdentity) {
  return `<section class="doc-page ${modeClass('QUOTATION')} q-cover">${letterhead(biz)}<div class="q-cover-body">${esc(m.coverLetter ?? '').replace(/\n/g, '<br>')}</div></section>`;
}

  function quotationPage(m: NormalizedDocument, biz: BusinessIdentity) {
   const validity = m.expiryDate ? `<p class="q-validity"><b>Valid Until:</b> ${esc(m.expiryDate)}</p>` : '';
   const businessTerms = m.terms?.trim() ? `<section class="q-terms"><b>Business Terms</b><div>${esc(m.terms).replace(/\n/g, '<br>')}</div></section>` : '';
   const signature = biz.signatureDataUrl ? `<img class="doc-signature" src="${esc(biz.signatureDataUrl)}" alt="">` : '';
   // The shared body from renderA4Body() ends with renderFooter(), which emits a
   // .doc-sign signature table and "For / Authorised Signatory". Quotation renders
   // its OWN signature via q-profile-signature, so strip the entire footer — from
   // the first footer element (.doc-foot-block or .doc-sign) to the end.
   const body = renderA4Body(m, biz)
     .replace(/<div class="doc-title">QUOTATION<\/div>/, '')
     .replace(/<div class="doc-foot-block">[\s\S]*$/, '')
     .replace(/<table class="doc-sign">[\s\S]*$/, '');
   const signatureRow = `<div class="q-profile-signature">For ${esc(biz.legalName)}${signature}<div>Authorised Signatory</div></div>`;
   return `<section class="doc-page ${modeClass('QUOTATION')} q-quotation"><div class="q-visible-title">QUOTATION · ${esc(m.docNumber)}</div>${body}${validity}${businessTerms}${signatureRow}</section>`;
 }

export function renderQuotationPackage(m: NormalizedDocument, biz: BusinessIdentity, assetsBaseUrl = ''): string {
  const pkg = buildQuotationPrintPackage({
    letterhead: { companyName: biz.legalName, addressLines: biz.addressLines, ...(biz.logoDataUrl ? { logoUrl: biz.logoDataUrl } : {}), ...(biz.gstin ? { gstin: biz.gstin } : {}) },
    signature: { companyName: biz.legalName, ...(biz.signatureDataUrl ? { imageUrl: biz.signatureDataUrl } : {}), label: 'Authorised Signatory' },
    totals: { grandTotalPaise: m.totals.grandTotalPaise } as never,
    coverLetter: { enabled: m.coverLetterEnabled === true, content: m.coverLetter ?? '' },
    ...(m.terms ? { businessTerms: { content: m.terms } } : {}),
  });
  const pages = pkg.pages.map((page) => page.kind === 'cover-letter' ? coverPage(m, biz) : quotationPage(m, biz)).join('');
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Quotation ${esc(m.docNumber)}</title><style id="doc">${a4Stylesheet(assetsBaseUrl)}
.q-cover-body { margin-top: 25mm; white-space: pre-wrap; font-size: 11pt; }
.q-letterhead { min-height: 20mm; font-size: 8pt; }
.q-letterhead strong { display: block; font-size: 11pt; }
.q-validity { margin: 0 0 2mm; font-size: 8pt; }
.q-terms { margin-top: 4mm; font-size: 8pt; }
.q-profile-signature { margin-top: 8mm; text-align: right; }
.q-profile-signature .doc-signature { margin-left: auto; }
  .q-quotation > .doc-sign,
  .q-quotation > .doc-computed,
  .q-quotation > .doc-foot-block { display: none; }
.q-visible-title { font-weight: 700; font-size: 13pt; margin-bottom: 2mm; }
@media print {
  @page { size: A4 portrait; margin: 0; }
  html, body { width: 210mm; }
  .q-cover, .q-quotation { min-height: ${A4_HEIGHT_MM}mm; break-inside: avoid; page-break-inside: avoid; }
  .q-cover { break-after: page; page-break-after: always; }
}
</style></head><body>${pages}</body></html>`;
}

export function renderQuotationPreview(m: NormalizedDocument, biz: BusinessIdentity, assetsBaseUrl = '', scale = 1): string {
  const html = renderQuotationPackage(m, biz, assetsBaseUrl);
  return html.replace('</style></head>', `</style><style id="q-preview">body { width: ${A4_WIDTH_PX * scale}px; } .doc-page { transform: scale(${scale}); transform-origin: top left; } .q-cover, .q-quotation { margin-bottom: 12px; }</style></head>`);
}

